import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { OrderProduct, Shopper, SetProduct, CustomValue, PartialOrder } from 'common/types';
import { updateActivePartialOrder } from 'actions';
import { ReduxState } from 'services/types';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import TextField from '@mui/material/TextField';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';

interface OwnProps {
	product: OrderProduct | SetProduct;
	shopper: Shopper;
	productId: string;
	setIndex: null | number;
	name: string;
	value: number | string;
	type: string;
	disabled: boolean;
}

interface StateToProps {
	products: OrderProduct[];
	showError: boolean;
}

interface DispatchProps {
	updateActivePartialOrder: (partialOrder: PartialOrder) => void;
}

type InputEventType = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

type Props = StateToProps & DispatchProps & OwnProps & WithStyles<typeof styles>;

export const ProductCustomValueInput = (props: Props) => {
	const { classes, product, products, productId, setIndex } = props;
	const [value, setValue] = useState<string | number>(props.value);

	useEffect(() => {
		setValue(props.value);
	}, [props.value]);

	const handleInputChange = (e: InputEventType) => {
		setValue(e.target.value);
	};

	const handleSave = async (e: InputEventType) => {
		const name = e.target.name;
		let customValues: CustomValue[] = [];
		if (!product.customValues) {
			customValues.push({ name, value });
		} else if (product.customValues.filter((cv) => cv.name === name).length === 0) {
			customValues = [...product.customValues, { name, value }];
		} else {
			customValues = product.customValues.map((cv) => (cv.name === name ? { name, value } : cv));
		}
		let updatedPartialProduct: Partial<OrderProduct | SetProduct> = { customValues };
		const productToUpdate = products.find((p) => p.id === productId);
		if (!productToUpdate) {
			return;
		}
		if (setIndex != null) {
			const setProducts = productToUpdate.setProducts.map((setProduct, index) => {
				if (index === setIndex) {
					return {
						...setProduct,
						...updatedPartialProduct,
					};
				}
				return setProduct;
			});
			updatedPartialProduct = { setProducts };
		}
		props.updateActivePartialOrder({
			products: [
				{
					id: productToUpdate.id,
					data: updatedPartialProduct,
				},
			],
		});
	};

	const showInputError = props.showError && !props.value && props.value !== 0;

	return (
		<TextField
			name={props.name}
			label={props.name}
			margin="none"
			variant="outlined"
			type={props.type}
			value={value}
			onChange={handleInputChange}
			onBlur={handleSave}
			error={showInputError}
			disabled={props.disabled}
			size="small"
			classes={{
				root: classes.formRoot,
			}}
			InputLabelProps={{
				shrink: true,
			}}
		/>
	);
};

const mapStateToProps = (state: ReduxState): StateToProps => {
	const products = RentalViewSelectors.orderProducts(state);
	const showError = state.view.showRentalViewFormErrors;
	return {
		products,
		showError,
	};
};

const styles = (theme: Theme) =>
	createStyles({
		formRoot: {
			marginTop: '0px !important',
			width: '100%',
		},
		withoutMargin: {
			marginTop: '0px',
		},
		extraMargin: {
			marginTop: '8px',
			maxWidth: '50%',
		},
	});

const dispatchProps: DispatchProps = {
	updateActivePartialOrder,
};

export default withStyles(styles)(connect(mapStateToProps, dispatchProps)(ProductCustomValueInput));
